
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { startChatWithSeller } from '@/lib/actions';

// Define types for our data structures
export interface SellerProfile {
  id: string;
  username?: string;
  full_name?: string;
  email?: string | null;
  profile_image?: string;
  created_at: string;
  phone_number?: string;
}

export interface ProductData {
  id: number;
  title: string;
  description: string;
  price: number;
  currency: string;
  location: string;
  category_id?: number;
  category?: {
    id: number;
    name: string;
  };
  images?: string[];
  video_url?: string; // Added video_url
  created_at: string;
  updated_at?: string;
  is_premium?: boolean;
  premium_until?: string;
  is_sold?: boolean;
  user_id: string;
  seller?: SellerProfile;
  delivery_option?: string;
  stock?: number;
  shipping_cost?: number;
  condition?: string;
  phone?: string;
  phone_country_code?: string | null;
  whatsapp_enabled?: boolean | null;
  viber_enabled?: boolean | null;
  is_price_negotiable?: boolean;
  avg_rating?: number;  
  rating_count?: number;
  contact_email?: string; 
  is_authenticated_listing?: boolean; 
}

export function useProductDetail(productId: string | undefined) {
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch product details
  const { data: product, isLoading } = useQuery<ProductData>({
    queryKey: ['product', productId],
    queryFn: async () => {
      // First, get the basic listing data
      const { data: listing, error: listingError } = await supabase
        .from('listings')
        .select('*, category:category_id(name, id)') // video_url is included in '*'
        .eq('id', Number(productId))
        .single();
      
      if (listingError) throw listingError;
      
      const productData = listing as ProductData;
      
      // Then separately fetch the seller profile if listing has a user_id
      if (productData && productData.user_id) {
        const { data: sellerProfile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', productData.user_id)
          .single();
          
        if (!profileError && sellerProfile) {
          // Attach the seller profile to the listing data
          return {
            ...productData,
            seller: sellerProfile as SellerProfile
          };
        }
      }
      
      // Return the listing data even if we couldn't get the seller profile
      return productData;
    },
  });
  
  // Fetch if product is favorited
  const { data: isFavorited } = useQuery({
    queryKey: ['favorite', productId, user?.id],
    queryFn: async () => {
      if (!user) return false;
      
      const { data, error } = await supabase
        .from('favorites')
        .select('*')
        .eq('user_id', user.id)
        .eq('listing_id', Number(productId))
        .maybeSingle();
      
      if (error && error.code !== 'PGRST116') {
        console.error('Error checking favorite status:', error);
        return false;
      }
      
      return !!data;
    },
    enabled: !!user && isAuthenticated,
  });
  
  // Fetch related products
  const { data: relatedProducts } = useQuery({
    queryKey: ['relatedProducts', product?.category_id],
    queryFn: async () => {
      if (!product?.category_id) return [];
      
      const { data, error } = await supabase
        .from('listings')
        .select('*')
        .eq('category_id', Number(product.category_id))
        .neq('id', Number(productId))
        .eq('is_sold', false)
        .order('is_premium', { ascending: false }) // Premium listings first
        .order('created_at', { ascending: false }) // Then by newest
        .limit(4);
      
      if (error) throw error;
      return data;
    },
    enabled: !!product?.category_id,
  });
  
  // Toggle favorite mutation
  const toggleFavoriteMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('User not authenticated');
      if (!productId) throw new Error('Product ID is required');
      
      if (isFavorited) {
        // Remove from favorites
        const { error } = await supabase
          .from('favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('listing_id', Number(productId));
        
        if (error) throw error;
        return false;
      } else {
        // Add to favorites
        const { error } = await supabase
          .from('favorites')
          .insert({
            user_id: user.id,
            listing_id: Number(productId)
          });
        
        if (error) throw error;
        return true;
      }
    },
    onSuccess: (newFavoriteState) => {
      queryClient.invalidateQueries({ queryKey: ['favorite', productId, user?.id] });
      queryClient.invalidateQueries({ queryKey: ['userFavorites', user?.id] });
      
      toast({
        title: newFavoriteState ? 'Shtuar te të preferuarat' : 'Hequr nga të preferuarat',
        description: newFavoriteState 
          ? 'Kjo shpallje u shtua te të preferuarat tuaja.' 
          : 'Kjo shpallje u hoq nga të preferuarat tuaja.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Gabim',
        description: error.message || 'Ndodhi një gabim gjatë përditësimit të të preferuarave.',
        variant: 'destructive',
      });
    },
  });

  // Start chat with seller mutation
  const startChatMutation = useMutation({
    mutationFn: async () => {
      if (!user || !product || !product.user_id) {
        throw new Error('Missing required information to start chat');
      }
      
      // Check if we're trying to message ourselves
      if (user.id === product.user_id) {
        throw new Error('You cannot message yourself');
      }

      const chatId = await startChatWithSeller(
        user.id,
        product.user_id,
        product.id
      );
      
      return chatId;
    },
    onSuccess: (chatId) => {
      if (chatId) {
        // Navigate to messages page with the specific chat selected
        navigate(`/messages?chat=${chatId}`);
      } else {
        toast({
          title: 'Error',
          description: 'Could not start conversation',
          variant: 'destructive',
        });
      }
    },
    onError: (error: any) => {
      if (error.message === 'You cannot message yourself') {
        toast({
          title: 'Cannot send message',
          description: 'This is your own listing',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Error',
          description: error.message || 'There was an error starting the conversation',
          variant: 'destructive',
        });
      }
    },
  });

  // Functions for handling various actions
  const handleFavoriteToggle = () => {
    if (!isAuthenticated) {
      toast({
        title: 'Authentication required',
        description: 'Please login to add items to your favorites.',
        variant: 'destructive',
      });
      navigate('/auth');
      return;
    }
    
    toggleFavoriteMutation.mutate();
  };

  const handleStartChat = () => {
    if (!isAuthenticated) {
      toast({
        title: 'Authentication required',
        description: 'Please login to message the seller.',
        variant: 'destructive',
      });
      navigate('/auth');
      return;
    }
    
    // If this is the user's own listing, show a message
    if (product?.user_id === user?.id) {
      toast({
        title: 'Cannot message yourself',
        description: 'This is your own listing.',
        variant: 'destructive',
      });
      return;
    }
    
    startChatMutation.mutate();
  };

  // Handle getting category and subcategory name for Schema.org data
  const getProductStructuredData = () => {
    if (!product) return null;

    // Create the base structured data object
    const structuredData: {
      "@context": string;
      "@type": string;
      name: string;
      description: string;
      image?: string;
      offers: {
        "@type": string;
        priceCurrency: string;
        price: number;
        availability: string;
        seller: {
          "@type": string;
          name: string;
        }
      };
      category?: string;
      aggregateRating?: {
        "@type": string;
        ratingValue: number;
        reviewCount: number;
      };
    } = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.title,
      "description": product.description,
      "image": product.images && product.images.length > 0 ? product.images[0] : undefined,
      "offers": {
        "@type": "Offer",
        "priceCurrency": product.currency || "EUR",
        "price": product.price,
        "availability": product.is_sold ? "https://schema.org/SoldOut" : "https://schema.org/InStock",
        "seller": {
          "@type": "Person",
          "name": product.seller?.full_name || product.seller?.username || "Seller"
        }
      }
    };

    // Add category if available
    if (product.category && product.category.name) {
      structuredData.category = product.category.name;
    }

    // Only add rating if it exists
    if (product.avg_rating && product.rating_count) {
      structuredData.aggregateRating = {
        "@type": "AggregateRating",
        "ratingValue": product.avg_rating,
        "reviewCount": product.rating_count
      };
    }

    return structuredData;
  };

  // Check if this is an unauthenticated listing (has contact email but no user profile)
  const isUnauthenticatedListing = product?.contact_email && !product?.seller;

  return {
    product,
    isLoading,
    isFavorited,
    relatedProducts,
    handleFavoriteToggle,
    handleStartChat,
    formatPrice,
    formatDate,
    getProductStructuredData,
    isUnauthenticatedListing
  };
}

// Utility functions that can be exported for reuse
export const formatPrice = (price: number, currency: string = 'EUR'): string => {
  const formatter = new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return formatter.format(price);
};

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays < 1) {
    return 'Sot';
  } else if (diffDays === 1) {
    return 'Dje';
  } else if (diffDays < 7) {
    return `${diffDays} ditë më parë`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} ${weeks === 1 ? 'javë' : 'javë'} më parë`;
  } else {
    return date.toLocaleDateString('sq-AL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
};
